/**
 * Types and interfaces for user relationships
 */

/**
 * Possible relationship types between users
 */
export type RelationshipType = 
  // Family relationships
  | 'parent'
  | 'child'
  | 'sibling'
  | 'spouse'
  | 'partner'
  | 'cousin'
  | 'uncle-aunt'
  | 'niece-nephew'
  | 'grandparent'
  | 'grandchild'
  | 'in-law'
  
  // Friend relationships
  | 'friend'
  | 'best-friend'
  | 'roommate'
  
  // Work relationships
  | 'colleague'
  | 'manager'
  | 'direct-report'
  | 'client'
  | 'business-partner'
  
  // Romantic relationships
  | 'romantic-interest'
  | 'dating'
  | 'engaged'
  
  // Other
  | 'acquaintance'
  | 'neighbor'
  | 'classmate'
  | 'teacher-mentor'
  | 'student-mentee'
  | 'other';

/**
 * User relationship data structure
 */
export interface Relationship {
  userId: string;       // The user ID 
  relationToUserId: string;  // The ID of the person they have a relationship with
  relationType: RelationshipType;  // Type of relationship
  relationLabel?: string;  // Optional custom label (e.g., "College Roommate")
  createdAt: Date | string;  // When this relationship was first established
  updatedAt: Date | string;  // When this relationship was last updated
  strength?: number;    // Optional relationship strength (1-5)
  isBidirectional: boolean;  // Whether both parties acknowledge the relationship
  notes?: string;       // Private notes about the relationship
}

/**
 * Relationship group for visualization
 */
export interface RelationshipGroup {
  id: string;
  name: string;
  description?: string;
  relationships: string[];  // IDs of relationships in this group
  color?: string;  // Display color for the group
}

/**
 * Map relationship types to human-readable labels
 */
export const relationshipLabels: Record<RelationshipType, string> = {
  // Family
  'parent': 'Parent',
  'child': 'Child',
  'sibling': 'Sibling',
  'spouse': 'Spouse',
  'partner': 'Partner',
  'cousin': 'Cousin',
  'uncle-aunt': 'Uncle/Aunt',
  'niece-nephew': 'Niece/Nephew',
  'grandparent': 'Grandparent',
  'grandchild': 'Grandchild',
  'in-law': 'In-law',
  
  // Friends
  'friend': 'Friend',
  'best-friend': 'Best Friend',
  'roommate': 'Roommate',
  
  // Work
  'colleague': 'Colleague',
  'manager': 'Manager',
  'direct-report': 'Direct Report',
  'client': 'Client',
  'business-partner': 'Business Partner',
  
  // Romantic
  'romantic-interest': 'Romantic Interest',
  'dating': 'Dating',
  'engaged': 'Engaged',
  
  // Other
  'acquaintance': 'Acquaintance',
  'neighbor': 'Neighbor',
  'classmate': 'Classmate', 
  'teacher-mentor': 'Teacher/Mentor',
  'student-mentee': 'Student/Mentee',
  'other': 'Other'
};

/**
 * Group relationship types by category
 */
export const relationshipCategories = {
  'Family': [
    'parent', 'child', 'sibling', 'spouse', 'partner', 'cousin',
    'uncle-aunt', 'niece-nephew', 'grandparent', 'grandchild', 'in-law'
  ] as RelationshipType[],
  
  'Friends': [
    'friend', 'best-friend', 'roommate'
  ] as RelationshipType[],
  
  'Work': [
    'colleague', 'manager', 'direct-report', 'client', 'business-partner'
  ] as RelationshipType[],
  
  'Romantic': [
    'romantic-interest', 'dating', 'engaged'
  ] as RelationshipType[],
  
  'Other': [
    'acquaintance', 'neighbor', 'classmate', 'teacher-mentor', 'student-mentee', 'other'
  ] as RelationshipType[]
};

/**
 * Network request data structure
 */
export interface NetworkRequest {
   id?: string; // Document ID
   fromUserId: string; // User sending the request
   fromUserName: string; // Name of user sending the request
   fromUserEmail: string; // Email of user sending the request
   fromUserPhoto?: string | null; // Photo URL of user sending the request
   toUserId: string | null; // User receiving the request
   toUserName?: string | null; // Name of user receiving the request
   toUserEmail: string; // Email of user receiving the request
   toUserPhoto?: string | null; // Photo URL of user receiving the request
   status: 'pending' | 'accepted' | 'declined'; // Request status
   createdAt: Date | string; // When the request was sent
   updatedAt?: Date | string | null; // When the request was last updated
   lastResent?: Date | string | null; // When the request was last resent
   declinedAt?: Date | string | null; // When the request was declined
   direction?: 'sent' | 'received'; // Direction of the request (for UI display)
   eventId?: string | null; // ID of the event where they met (if applicable)
   eventTitle?: string | null; // Title of the event where they met
   relationshipType?: RelationshipType | null; // Proposed relationship type
   relationshipLabel?: string | null; // Optional custom label for the relationship
   message?: string | null; // Optional message from requester
} 